import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, Checkbox } from '@material-ui/core';
import { Form, Input } from 'antd';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

/**
 * StepBookerDetails - Step 1: Booker information
 * Uses placeholders instead of labels above inputs
 * Errors shown consolidated above the continue button
 */
const StepBookerDetails = ({ onContinue, form }) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState([]);
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Sync local state with form value (for localStorage restore)
  // Only check if marketing_consent is true AND consent_version is 'v1_full'
  useEffect(() => {
    const marketingValue = form?.getFieldValue('marketing_consent');
    const versionValue = form?.getFieldValue('consent_version');
    if (marketingValue === true && versionValue === 'v1_full') {
      setMarketingConsent(true);
    }
  }, [form]);

  const handleContinue = async () => {
    try {
      // Validate only the fields for this step
      await form.validateFields(['first_name', 'last_name', 'email', 'email_verification', 'phone']);
      setErrors([]);
      onContinue();
    } catch (errorInfo) {
      // Collect all error messages
      const errorMessages = errorInfo.errorFields?.map(field => ({
        name: field.name[0],
        message: field.errors[0]
      })) || [];
      setErrors(errorMessages);
    }
  };

  // Clear errors when user starts typing
  const handleFieldChange = () => {
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  return (
    <div className="step-booker-details" role="region" aria-label={t('reservation.steps.bookerDetails.title')}>
      <div className="wizard-info-bar wizard-info-bar--highlight wizard-info-bar--compact" role="note">
        <strong>{t('package.modals.passengersChange.heading')}: </strong>
        {t('reservation.steps.bookerDetails.notice')}
      </div>

      <Grid container spacing={2}>
        {/* First Name */}
        <Grid item xs={6} sm={6}>
          <Form.Item
            name="first_name"
            rules={[{ required: true, message: t('reservation.steps.bookerDetails.errors.firstNameRequired') }]}
          >
            <Input
              className="wizard-input"
              placeholder={t('reservation.steps.bookerDetails.firstName')}
              size="large"
              aria-label={t('reservation.steps.bookerDetails.firstName')}
              onChange={handleFieldChange}
            />
          </Form.Item>
        </Grid>

        {/* Last Name */}
        <Grid item xs={6} sm={6}>
          <Form.Item
            name="last_name"
            rules={[{ required: true, message: t('reservation.steps.bookerDetails.errors.lastNameRequired') }]}
          >
            <Input
              className="wizard-input"
              placeholder={t('reservation.steps.bookerDetails.lastName')}
              size="large"
              aria-label={t('reservation.steps.bookerDetails.lastName')}
              onChange={handleFieldChange}
            />
          </Form.Item>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Email */}
        <Grid item xs={6} sm={6}>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: t('reservation.steps.bookerDetails.errors.emailRequired') },
              { type: "email", message: t('reservation.steps.bookerDetails.errors.emailInvalid') },
              {
                pattern: /^[a-zA-Z0-9._+~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z0-9]{2,}$/i,
                message: t('reservation.steps.bookerDetails.errors.emailInvalid'),
              },
            ]}
          >
            <Input
              className="wizard-input"
              placeholder={t('reservation.steps.bookerDetails.email')}
              type="email"
              size="large"
              aria-label={t('reservation.steps.bookerDetails.email')}
              onChange={handleFieldChange}
            />
          </Form.Item>
        </Grid>

        {/* Email Verification */}
        <Grid item xs={6} sm={6}>
          <Form.Item
            name="email_verification"
            dependencies={["email"]}
            rules={[
              { required: true, message: t('reservation.steps.bookerDetails.errors.emailVerificationRequired') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("email") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('reservation.steps.bookerDetails.errors.emailMismatch')));
                },
              }),
            ]}
          >
            <Input
              className="wizard-input"
              placeholder={t('reservation.steps.bookerDetails.emailVerification')}
              type="email"
              size="large"
              aria-label={t('reservation.steps.bookerDetails.emailVerification')}
              onChange={handleFieldChange}
            />
          </Form.Item>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Phone */}
        <Grid item xs={12} sm={6}>
          <Form.Item
            name="phone"
            rules={[
              { required: true, message: t('reservation.steps.bookerDetails.errors.phoneRequired') },
              {
                pattern: /^0[5-9][0-9]{8}$/,
                message: t('reservation.steps.bookerDetails.errors.phoneInvalid'),
              },
            ]}
          >
            <Input
              className="wizard-input tel-input"
              placeholder={t('reservation.steps.bookerDetails.phone')}
              type="tel"
              size="large"
              aria-label={t('reservation.steps.bookerDetails.phone')}
              onChange={handleFieldChange}
            />
          </Form.Item>
        </Grid>
      </Grid>

      {/* Marketing Consent Checkbox */}
      <div
        className="booker-marketing-consent booker-marketing-clickable"
        onClick={() => {
          const newValue = !marketingConsent;
          setMarketingConsent(newValue);
          form.setFieldsValue({
            marketing_consent: newValue,
            consent_version: newValue ? 'v1_full' : null
          });
        }}
      >
        <Checkbox
          color="primary"
          className="marketing-checkbox"
          checked={marketingConsent}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            setMarketingConsent(e.target.checked);
            form.setFieldsValue({
              marketing_consent: e.target.checked,
              consent_version: e.target.checked ? 'v1_full' : null
            });
          }}
        />
        <Form.Item
          name="marketing_consent"
          valuePropName="checked"
          className="wizard-hidden-field"
        >
          <input type="hidden" />
        </Form.Item>
        <Form.Item
          name="consent_version"
          className="wizard-hidden-field"
        >
          <Input type="hidden" />
        </Form.Item>
        <span
          className="marketing-consent-text"
          onClick={() => {
            const newValue = !marketingConsent;
            setMarketingConsent(newValue);
            form.setFieldsValue({
              marketing_consent: newValue,
              consent_version: newValue ? 'v1_full' : null
            });
          }}
        >
          {t('reservation.steps.bookerDetails.marketingConsent')}
        </span>
      </div>

      {/* Error Display - Above Button */}
      {errors.length > 0 && (
        <div className="step-error-container" role="alert" aria-live="polite">
          <div className="step-error-title">
            <ErrorOutlineIcon className="step-error-icon" aria-hidden="true" />
            {t('reservation.steps.bookerDetails.errors.fixErrors')}
          </div>
          <ul className="step-error-list" aria-label={t('reservation.steps.bookerDetails.errors.errorsList')}>
            {errors.map((error, idx) => (
              <li key={idx} className="step-error-item">
                {error.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Continue Button */}
      <button
        type="button"
        className="step-continue-btn"
        onClick={handleContinue}
        aria-label={t('common.buttons.continue')}
      >
        {t('common.buttons.continue')}
      </button>
    </div>
  );
};

export default StepBookerDetails;
